import { useStoreActiveCameras } from "@/components/common/view-runtime-context";
import { useCameraParametersIfAvailable } from "@/components/r3f/utils/camera-parameters";
import type { MiniMapActions } from "@/components/r3f/utils/minimap/minimap";
import { useSceneBackgroundColor } from "@/components/r3f/utils/scene-background";
import { selectModeInitialState } from "@/store/mode-selectors";
import {
  selectShouldSyncCamerasOnWaypoint,
  setCompareElementId,
  setSyncCamerasOnWaypoint,
  setSyncCamerasRotation,
} from "@/store/modes/walk-mode-slice";
import { setActiveElement } from "@/store/selections-slice";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { SceneFilter } from "@/types/scene-filter";
import { CameraViewType } from "@/utils/cam-view";
import { selectPanoCameraTransform } from "@/utils/camera-transform";
import {
  selectHasValidPose,
  useNonExhaustiveEffect,
  View,
} from "@faro-lotv/app-component-toolbox";
import {
  isIElementImg360,
  type IElement,
  type IElementGenericImgSheet,
} from "@faro-lotv/ielement-types";
import {
  DEFAULT_PERSP_FOV,
  reproportionCamera,
  type SupportedCamera,
} from "@faro-lotv/lotv";
import { useThree } from "@react-three/fiber";
import type { JSX, RefObject } from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import { PerspectiveCamera, Vector3, type Camera } from "three";
import { useOverlayElements } from "../overlay-elements-context";
import { SplitMinMap } from "../split-mode/split-minimap";
import type { SplitInitialState, SplitState } from "../split-mode/split-state";
import { selectBestModelCameraFor360 } from "../walk-mode/animations/pano-to-model";
import { selectIsPanoAligned } from "../walk-mode/walk-scene-selectors";
import { FlySplitView, type FlySplitViewActions } from "./fly-split-view";
import type { FlyWalkWaypoint } from "./flywalk-types";
import { useFlySplitSceneCameras } from "./use-fly-split-scene-cameras";

/** @returns A component that renders two distinct views on elements of the same project. */
export function FlySplitScene({
  mainScene,
  mainWalkElement,
  compareScene,
  compareWalkElement,
  initialState,
  mainSceneFilter,
  compareSceneFilter,
}: SplitState & { initialState: SplitInitialState | undefined }): JSX.Element {
  const background = useSceneBackgroundColor();

  // Computing all cameras needed in splitscreen mode
  const { leftViewCameras, rightViewCameras } = useFlySplitSceneCameras();

  // This React state stores the last camera the user interacted with.
  // This active camera is then used to provide the 'sync rotation' functionality.
  const [activeCamera, setActiveCamera] = useState<Camera>(
    leftViewCameras.camera,
  );

  // Updating both left and right cameras according to a possible given deep link
  useCameraParametersIfAvailable(leftViewCameras.camera, initialState?.camera);
  useCameraParametersIfAvailable(
    rightViewCameras.camera,
    initialState?.rightCam,
  );

  const cameras = useMemo(
    () => [leftViewCameras.camera, rightViewCameras.camera],
    [leftViewCameras.camera, rightViewCameras.camera],
  );

  // Storing the active cameras in a context used to compute deep links on demand
  useStoreActiveCameras(cameras);

  const { firstScreen, secondScreen, miniMap, minimapCanvas } =
    useOverlayElements();
  const miniMapActions = useRef<MiniMapActions>(null);

  // The left camera is the default r3f camera used also for other modes.
  // On unmount, the 'View' component writes an invalid aspect ratio prop
  // to the camera. Therefore, on unmount we need to reproportion the left
  // camera according to the r3f canvas' size.
  useReproportionCameraOnUnmount(leftViewCameras.camera);

  const leftViewActions = useRef<FlySplitViewActions>(null);
  const rightViewActions = useRef<FlySplitViewActions>(null);

  // When the user finishes moving the left or the right camera, the minimap
  // is re-centered on the last camera moved.
  const onCameraMovedViaControls = useCallback(
    (pos: Vector3) => {
      miniMapActions.current?.centerCameraOn(pos);
    },
    [miniMapActions],
  );

  const {
    onMainWaypointClicked,
    onCompareWaypointClicked,
    placeholderClickedInMinimap,
    onLeftViewAnimationFinished,
    onRightViewAnimationFinished,
  } = useSyncCamerasOnWaypoint(
    mainSceneFilter,
    compareSceneFilter,
    leftViewCameras.camera,
    rightViewCameras.camera,
    leftViewActions,
    rightViewActions,
    mainScene.activeElement,
    compareScene.activeElement,
    mainScene.activeSheetFor2DNavigation,
    compareScene.activeSheetFor2DNavigation,
  );

  return (
    <>
      <View
        camera={leftViewCameras.camera}
        trackingElement={firstScreen}
        background={background}
        hasSeparateScene
      >
        <FlySplitView
          currentScene={mainScene}
          sceneFilter={mainSceneFilter}
          isSecondaryView={false}
          onWaypointClicked={onMainWaypointClicked}
          actions={leftViewActions}
          cameras={leftViewCameras}
          activeCamera={activeCamera}
          setActiveCamera={setActiveCamera}
          onCameraMovedViaControls={onCameraMovedViaControls}
          onAnimationEnded={onLeftViewAnimationFinished}
        />
      </View>
      <View
        camera={rightViewCameras.camera}
        trackingElement={secondScreen}
        background={background}
        hasSeparateScene
      >
        <FlySplitView
          currentScene={compareScene}
          sceneFilter={compareSceneFilter}
          isSecondaryView
          onWaypointClicked={onCompareWaypointClicked}
          actions={rightViewActions}
          cameras={rightViewCameras}
          activeCamera={activeCamera}
          setActiveCamera={setActiveCamera}
          onCameraMovedViaControls={onCameraMovedViaControls}
          onAnimationEnded={onRightViewAnimationFinished}
        />
      </View>
      {miniMap && secondScreen && (
        <SplitMinMap
          trackingElement={miniMap}
          canvasElement={minimapCanvas}
          actions={miniMapActions}
          mainWalkElement={mainWalkElement}
          compareWalkElement={compareWalkElement}
          mainSceneFilter={mainSceneFilter}
          compareSceneFilter={compareSceneFilter}
          leftCamera={leftViewCameras.camera}
          rightCamera={rightViewCameras.camera}
          onPlaceholderClicked={placeholderClickedInMinimap}
          onLeftCameraMovedTo={leftViewActions.current?.onCameraMovedTo}
          onRightCameraMovedTo={rightViewActions.current?.onCameraMovedTo}
        />
      )}
    </>
  );
}

type OnWaypointClicked = (waypoint: FlyWalkWaypoint) => void;

type UseSyncCamerasOnWaypointResult = {
  onMainWaypointClicked: OnWaypointClicked;
  onCompareWaypointClicked: OnWaypointClicked;
  placeholderClickedInMinimap: OnWaypointClicked;
  onLeftViewAnimationFinished(): void;
  onRightViewAnimationFinished(): void;
};

type ViewActionsRef = RefObject<FlySplitViewActions | null>;

function useSyncCamerasOnWaypoint(
  mainSceneFilter: SceneFilter,
  compareSceneFilter: SceneFilter,
  leftViewCamera: SupportedCamera,
  rightViewCamera: SupportedCamera,
  leftViewActions: ViewActionsRef,
  rightViewActions: ViewActionsRef,
  mainSceneActiveElement: IElement,
  compareSceneActiveElement: IElement,
  mainSheet?: IElementGenericImgSheet,
  compareSheet?: IElementGenericImgSheet,
): UseSyncCamerasOnWaypointResult {
  const dispatch = useAppDispatch();
  const { getState } = useAppStore();

  // Tracking animations in both the left and the right view.
  // In some cases, when an animation is finished in both views,
  // we need to enable 'sync rotation' only after both animations are finished.
  const waitingLeftToSetSyncRotOn = useRef(false);
  const waitingRightToSetSyncRotOn = useRef(false);

  const enableSyncRotationAfterAnimation = useCallback(() => {
    waitingLeftToSetSyncRotOn.current = true;
    waitingRightToSetSyncRotOn.current = true;
  }, []);

  const onLeftViewAnimationFinished = useCallback(() => {
    if (waitingLeftToSetSyncRotOn.current) {
      waitingLeftToSetSyncRotOn.current = false;
      if (!waitingRightToSetSyncRotOn.current) {
        dispatch(setSyncCamerasRotation(true));
      }
    }
  }, [dispatch]);

  const onRightViewAnimationFinished = useCallback(() => {
    if (waitingRightToSetSyncRotOn.current) {
      waitingRightToSetSyncRotOn.current = false;
      if (!waitingLeftToSetSyncRotOn.current) {
        dispatch(setSyncCamerasRotation(true));
      }
    }
  }, [dispatch]);

  // Callback triggered when the user clicks a waypoint in the right view and
  // 'sync waypoint' is on: the left camera is animated to the clicked position
  // and rotated to the right camera's pose.
  const moveLeftCameraToPositionAndSyncRot = useCallback(
    (pos: Vector3) => {
      if (!(rightViewCamera instanceof PerspectiveCamera)) {
        return;
      }
      leftViewActions.current?.onCameraMovedTo(
        pos,
        rightViewCamera.quaternion.clone(),
        DEFAULT_PERSP_FOV,
      );
    },
    [rightViewCamera, leftViewActions],
  );

  // Callback triggered when the user clicks a waypoint in the left view and
  // 'sync waypoint' is on: the right camera is animated to the clicked position
  // and rotated to the left camera's pose.
  const moveRightCameraToPositionAndSyncRot = useCallback(
    (pos: Vector3) => {
      if (!(leftViewCamera instanceof PerspectiveCamera)) {
        return;
      }
      rightViewActions.current?.onCameraMovedTo(
        pos,
        leftViewCamera.quaternion.clone(),
        DEFAULT_PERSP_FOV,
      );
    },
    [leftViewCamera, rightViewActions],
  );

  const shouldSyncCamerasOnWaypoint = useAppSelector(
    selectShouldSyncCamerasOnWaypoint,
  );

  // Whenever a waypoint is clicked in the minimap, 'sync views on waypoint' is set to true
  // and the cameras are animated to the clicked waypoint.
  const placeholderClickedInMinimap = useCallback(
    (waypoint: FlyWalkWaypoint) => {
      if (!shouldSyncCamerasOnWaypoint) {
        dispatch(setSyncCamerasOnWaypoint(true));
      }
      leftViewActions.current?.onPanoClickedInMinimap(waypoint);
      rightViewActions.current?.onPanoClickedInMinimap(waypoint);
    },
    [leftViewActions, rightViewActions, shouldSyncCamerasOnWaypoint, dispatch],
  );

  const onMainWaypointClicked = useCallback(
    (waypoint: FlyWalkWaypoint) => {
      // If the scene filter is pano, change the active element.
      // If not, simply move the camera.
      if (mainSceneFilter === SceneFilter.Pano) {
        dispatch(setActiveElement(waypoint.id));
      } else {
        const position = selectBestModelCameraFor360(
          waypoint,
          mainSheet,
        )(getState());
        leftViewActions.current?.onCameraMovedTo(position);
      }
      // If waypoints should be synced across views, move the right camera to the corresponding position and rotation.
      if (shouldSyncCamerasOnWaypoint) {
        // Animating the right camera as well, enabling sync rot at end of animation
        enableSyncRotationAfterAnimation();
        const { position } = selectPanoCameraTransform(
          waypoint,
          mainSheet,
        )(getState());
        const thePosition = new Vector3().fromArray(position);
        if (compareSceneFilter === SceneFilter.Pano) {
          dispatch(setCompareElementId(waypoint.id));
          rightViewActions.current?.setCameraView({
            type: CameraViewType.perspective,
            position: thePosition,
            rotation: leftViewCamera.quaternion.clone(),
            zoomLevel: DEFAULT_PERSP_FOV,
          });
        } else {
          moveRightCameraToPositionAndSyncRot(thePosition);
        }
      }
    },
    [
      mainSceneFilter,
      shouldSyncCamerasOnWaypoint,
      dispatch,
      mainSheet,
      getState,
      leftViewActions,
      rightViewActions,
      leftViewCamera,
      compareSceneFilter,
      moveRightCameraToPositionAndSyncRot,
      enableSyncRotationAfterAnimation,
    ],
  );

  const onCompareWaypointClicked = useCallback(
    (waypoint: FlyWalkWaypoint) => {
      // If the scene filter is pano, change the active element.
      // If not, simply move the camera.
      if (compareSceneFilter === SceneFilter.Pano) {
        dispatch(setCompareElementId(waypoint.id));
      } else {
        const position = selectBestModelCameraFor360(
          waypoint,
          compareSheet,
        )(getState());
        rightViewActions.current?.onCameraMovedTo(position);
      }
      // If waypoints should be synced across views, move the left camera to the corresponding position and rotation.
      if (shouldSyncCamerasOnWaypoint) {
        // Animating the left camera as well, enabling sync rot at end of animation
        enableSyncRotationAfterAnimation();
        const { position } = selectPanoCameraTransform(
          waypoint,
          compareSheet,
        )(getState());
        const thePosition = new Vector3().fromArray(position);
        if (mainSceneFilter === SceneFilter.Pano) {
          dispatch(setActiveElement(waypoint.id));
          leftViewActions.current?.setCameraView({
            type: CameraViewType.perspective,
            position: thePosition,
            rotation: rightViewCamera.quaternion.clone(),
            zoomLevel: DEFAULT_PERSP_FOV,
          });
        } else {
          moveLeftCameraToPositionAndSyncRot(thePosition);
        }
      }
    },
    [
      compareSceneFilter,
      shouldSyncCamerasOnWaypoint,
      dispatch,
      compareSheet,
      getState,
      rightViewActions,
      enableSyncRotationAfterAnimation,
      mainSceneFilter,
      leftViewActions,
      rightViewCamera.quaternion,
      moveLeftCameraToPositionAndSyncRot,
    ],
  );

  function canSyncRotation(element: IElement): boolean {
    return (
      isIElementImg360(element) &&
      selectHasValidPose(element)(getState()) &&
      selectIsPanoAligned(element)(getState())
    );
  }

  /**
   * lock screens at opening if they can be locked automatically, unless the
   * initial state of the mode, e.g. a deep link or a restored history entry, sets it
   */
  useNonExhaustiveEffect(() => {
    if (
      selectModeInitialState(getState())?.syncRot === undefined &&
      (mainSceneFilter === SceneFilter.Cad ||
        canSyncRotation(mainSceneActiveElement)) &&
      canSyncRotation(compareSceneActiveElement)
    ) {
      dispatch(setSyncCamerasRotation(true));
    }

    return () => {
      // always unlock when leaving split screen view
      dispatch(setSyncCamerasRotation(false));
    };
  }, []);

  return {
    onMainWaypointClicked,
    onCompareWaypointClicked,
    placeholderClickedInMinimap,
    onLeftViewAnimationFinished,
    onRightViewAnimationFinished,
  };
}

function useReproportionCameraOnUnmount(camera: Camera): void {
  const size = useThree((s) => s.size);

  useNonExhaustiveEffect(
    () => () => {
      reproportionCamera(camera, size.width / size.height);
    },
    [camera],
  );
}
